import { h, Component, ComponentChildren, cloneElement } from 'preact'
import cn from 'classnames'
import { Scrollbars } from 'preact-custom-scrollbars'
import { OutsideClickWrapper } from '../OutsideClickWrapper'
import { Input, iInputProps } from '../Input'
import s from './Select.css'
import { logError } from '../../utils/logger'

interface iProps extends iInputProps {
  onChange: (e: string) => void
  children: ComponentChildren
}

interface iState {
  optionsVisibility: boolean
  placeholder: string
}

export class Select extends Component<iProps, iState> {
  state = {
    optionsVisibility: false,
    placeholder: this.props.placeholder || ''
  }

  get filteredChildren () {
    const { children, value } = this.props

    try {
      return (children as any).filter((child: any) => child.props.label.toLowerCase().includes(String(value).toLowerCase()))
    } catch (e) {
      logError('filteredChildren', e)
      return []
    }
  }

  get isOpen () {
    const { optionsVisibility } = this.state

    return optionsVisibility && this.filteredChildren.length > 0
  }

  showOptions = () => {
    this.setState({
      optionsVisibility: true
    })
  }

  hideOptions = () => {
    this.setState({
      optionsVisibility: false,
      placeholder: this.props.placeholder
    })
  }

  toggleOptions = () => {
    this.setState({
      optionsVisibility: !this.state.optionsVisibility
    })
  }

  onInput = (e: Event) => {
    const { onChange } = this.props
    const { value } = e.target as HTMLInputElement

    this.showOptions()
    onChange(value)
  }

  onInputKeyUp = (e: KeyboardEvent) => {
    const { onChange } = this.props

    switch (e.key) {
      case 'Enter':
        const [ firstChild ] = this.filteredChildren

        if (firstChild) {
          onChange(firstChild.props.label)
          this.hideOptions()
        }
        break
      case 'Escape':
        this.hideOptions()
        break
    }
  }

  onOptionMouseEnter = (placeholder: string) => {
    this.setState({ placeholder })
  }

  onOptionMouseLeave = () => {
    this.setState({
      placeholder: this.props.placeholder
    })
  }

  handleOption = (value: string) => {
    this.props.onChange(value)
    this.hideOptions()
  }

  renderOptions = () => {
    const filteredChildren = this.filteredChildren

    if (this.isOpen) {
      const { value } = this.props

      return (
        <div
          className={s.Select__list}
          role="listbox"
        >
          <Scrollbars
            autoHeight
            autoHeightMin={0}
            autoHeightMax={375}
          >
            {filteredChildren.map((child: any) => cloneElement(child, {
              ...child.props,
              role: 'option',
              selected: child.props.label === value,
              onMouseEnter: () => this.onOptionMouseEnter(child.props.label),
              onMouseLeave: this.onOptionMouseLeave,
              onClick: () => this.handleOption(child.props.label)
            }))}
          </Scrollbars>
        </div>
      )
    }
  }

  render () {
    const {
      onChange,
      className,
      value,
      ...props
    } = this.props

    const { placeholder } = this.state

    return (
      <OutsideClickWrapper
        className={cn(s.Select, className)}
        handler={this.hideOptions}
      >
        <Input
          className={s.Select__input}
          onInput={this.onInput}
          onKeyUp={this.onInputKeyUp}
          {...props}
          value={value}
          placeholder={placeholder}
        />
        <button
          type="button"
          className={cn(s.Select__arrow, {
            [s.Select__arrow_up]: this.isOpen
          })}
          onClick={this.toggleOptions}
        />

        {this.renderOptions()}
      </OutsideClickWrapper>
    )
  }
}
